import { assign, Interpreter, Machine, MachineConfig, Typestate } from "xstate";

export type TimerContext = {
  /** Start date of the timer (in ms since epoch) */
  start?: number;
  /** End date of the timer (in ms since epoch) */
  end?: number;
  /** The timer duration (in seconds) */
  duration?: number;
  /** The timer elapsed time since start (in seconds) */
  elapsed?: number;
  /** The timer interval between TICK events (in seconds) */
  interval?: number;
};

const initialTimerContext: TimerContext = {
  start: undefined,
  end: undefined,
  duration: undefined,
  elapsed: undefined,
  interval: undefined,
};

export type TimerState = {
  states: {
    new: {};
    ready: {};
    running: {};
    paused: {};
    expired: {};
  };
};

export type TimerEvent =
  | { type: "INIT"; start: number; end: number; interval: number }
  | { type: "START" }
  | { type: "PAUSE" }
  | { type: "RESUME" }
  | { type: "STOP" }
  | { type: "TICK" }
  | { type: "RESET" };

export type TimerService = Interpreter<
  TimerContext,
  any,
  TimerEvent,
  Typestate<TimerContext>
>;

export const timerMachineConfig: MachineConfig<
  TimerContext,
  TimerState,
  TimerEvent
> = {
  id: "timer",
  context: { ...initialTimerContext },
  initial: "new",
  on: {
    RESET: {
      target: "new",
      actions: "reset",
    },
  },
  states: {
    new: {
      on: {
        INIT: { target: "ready", actions: "init" },
      },
    },
    ready: {
      on: {
        START: "running",
      },
    },
    running: {
      invoke: {
        src: "tick",
      },
      always: { target: "expired", cond: "isExpired" },
      on: {
        PAUSE: "paused",
        TICK: { actions: "updateElapsed" },
      },
    },
    paused: {
      on: {
        RESUME: "running",
      },
    },
    expired: {},
  },
};

export const timerMachine = Machine<TimerContext, TimerState, TimerEvent>(
  timerMachineConfig,
  {
    actions: {
      init: assign((context, event) => {
        if (event.type !== "INIT") return context;

        return {
          duration: Math.floor((event.end - event.start) / 1000),
          elapsed: Math.floor((Date.now() - event.start) / 1000),
          interval: event.interval,
          end: event.end,
          start: event.start,
        };
      }),
      reset: assign<TimerContext, TimerEvent>({
        start: undefined,
        end: undefined,
        duration: undefined,
        elapsed: undefined,
        interval: undefined,
      }),
      updateElapsed: assign({
        elapsed: (context, _event) =>
          Math.floor((Date.now() - (context.start as number)) / 1000),
      }),
    },
    services: {
      tick: (context, _event) => (callback, _onReceive) => {
        const interval = setInterval(() => {
          callback("TICK");
        }, (context.interval as number) * 1000);

        return () => {
          clearInterval(interval);
        };
      },
    },
    guards: {
      isExpired: (context, _event) => Date.now() > (context.end as number),
    },
  }
);
